import { combineReducers, createNextState, type Reducer, type UnknownAction } from '@reduxjs/toolkit';

import type { RequestMeta } from 'Core/actions/request';
import { createIsolatedReducer, getRequestMeta } from 'Core/reducers/common';
import { FacetValue, type FacetValueBase } from 'Models/index';
import { equalByMany } from 'Utils/array';

import lastSearchOptions from './lastSearchOptions';
import request from './request/index';
import response from './response';
import retriesCount from './retriesCount';
import serverRedirect from './serverRedirect';

const search = createIsolatedReducer(
  null,
  combineReducers({
    lastSearchOptions,
    request,
    response,
    retriesCount,
    serverRedirect,
  }),
);

// Single source of truth for the loose shape of an action that drives search
// (consumed by both this reducer and the search epic). All flags travel via
// `meta`; pre-RTK actions that flattened these onto the action itself have
// been migrated.
export type SearchTriggerAction = UnknownAction & {
  meta?: Partial<RequestMeta>;
};

const rootReducer: Reducer<ReturnType<typeof search>, SearchTriggerAction> = (state, action) => {
  const meta = getRequestMeta(action);
  return search(fixRequestDueToResponse(state, Boolean(meta.mayDiscardValue)), action);
};

export default rootReducer;

type FixableState = {
  request: { selection: FacetValueBase[]; query: string };
  response: { facets: { selection: FacetValueBase[] }; query: string };
};

function fixRequestDueToResponse<S extends FixableState>(state: S | undefined, fix: boolean): S | undefined {
  if (!state || !fix) {
    return state;
  }
  return createNextState(state, (draft) => {
    const { selection } = draft.request;
    const responseSelection = draft.response.facets.selection;

    if (!equalByMany(selection, responseSelection, FacetValue.termKey, 'isUnique')) {
      draft.request.selection = responseSelection;
    }
    draft.request.query = draft.response.query;
  });
}
